import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import type {
  ApplicationInsights as ApplicationInsightsType,
  IConfiguration,
} from '@microsoft/applicationinsights-web';
import { EnvironmentService } from '@services/environment/environment.service';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ApplicationInsightsService {
  private appInsights: ApplicationInsightsType | null = null;
  private readonly isBrowser: boolean;

  constructor() {
    const platformId = inject(PLATFORM_ID);
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      // Initialize asynchronously to avoid pulling the ApplicationInsights bundle
      // into the initial application bundle. The dynamic import will be
      // tree-shaken into a separate chunk and only loaded in the browser.
      void this.initAppInsights();
    }
  }

  private async initAppInsights(): Promise<void> {
    try {
      const config = {
        instrumentationKey:
          EnvironmentService.environment.appInsights?.instrumentationKey || '',
        enableAutoRouteTracking: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: false,
      } as unknown as IConfiguration;

      // If a test or global script has provided a global ApplicationInsights constructor
      // (e.g. tests mocking `window.ApplicationInsights`), prefer that to avoid
      // performing a dynamic import in test environments and to keep coverage stable.
      const win = globalThis as
        | {
            ApplicationInsights?: new (opts: {
              config: IConfiguration;
            }) => ApplicationInsightsType;
          }
        | undefined;

      if (win?.ApplicationInsights) {
        this.appInsights = new win.ApplicationInsights({ config });
        this.appInsights.loadAppInsights();
        return;
      }

      /* istanbul ignore next */
      /* istanbul ignore next */
      const mod = await import('@microsoft/applicationinsights-web');
      /* istanbul ignore next */
      const { ApplicationInsights } = mod;

      /* istanbul ignore next */
      this.appInsights = new ApplicationInsights({ config });

      /* istanbul ignore next */
      this.appInsights.loadAppInsights();
    } catch (e) {
      this.appInsights = null;
      console.warn('Application Insights failed to initialize', e);
    }
  }

  logPageView(name?: string, url?: string) {
    if (!this.isBrowser) return;
    this.appInsights?.trackPageView({
      name: name,
      uri: url,
    });
  }

  logEvent(name: string, properties?: { [key: string]: unknown }) {
    if (!this.isBrowser) return;
    this.appInsights?.trackEvent({ name: name }, properties);
  }

  logMetric(
    name: string,
    average: number,
    properties?: { [key: string]: unknown },
  ) {
    if (!this.isBrowser) return;
    this.appInsights?.trackMetric({ name: name, average: average }, properties);
  }

  logException(exception: Error, severityLevel?: number) {
    if (!this.isBrowser) return;
    this.appInsights?.trackException({
      exception: exception,
      severityLevel: severityLevel,
    });
  }

  logTrace(
    message: string,
    properties?: { [key: string]: unknown },
    severityLevel?: number,
  ) {
    if (!this.isBrowser) return;
    this.appInsights?.trackTrace(
      { message: message, severityLevel: severityLevel },
      properties,
    );
  }

  logDependencyData(
    name: string,
    url: string,
    duration: number,
    success: boolean,
    responseCode: number,
    method?: string,
  ) {
    if (!this.isBrowser) return;
    this.appInsights?.trackDependencyData({
      id: '',
      name: name,
      target: url,
      duration: duration,
      success: success,
      responseCode: responseCode,
      data: method || '',
      type: 'Robyn UI',
    });
  }
}
